import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isApiError,
  memberRefusalMessage,
  validationField,
} from "@/lib/api/client";
import { errorCopy } from "@/lib/api/error-copy";
import { mediaUrl } from "@/lib/api/studio";
import type { CharacterLifecycle } from "@/lib/studio/types";
import { cn } from "@/lib/cn";

export function FirstPersistenceDisclosure() {
  return (
    <aside className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-3 text-sm leading-relaxed">
      <p className="font-medium">
        Saving or uploading makes this draft and its future contents visible to
        every organization member.
      </p>
    </aside>
  );
}

export function ScreenHeader({
  eyebrow,
  title,
  description,
  back,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  back?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 space-y-2">
        {back && (
          <Link
            to={back}
            className="inline-flex items-center gap-1.5 rounded-md text-sm text-fg-muted hover:text-fg"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back
          </Link>
        )}
        {eyebrow && (
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
            {eyebrow}
          </p>
        )}
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {title}
        </h1>
        {description && (
          <p className="max-w-2xl text-sm leading-relaxed text-fg-muted">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function QueryFailure({
  error,
  retry,
  title = "This page could not be loaded.",
  headingLevel = "h1",
}: {
  error: unknown;
  retry: () => void;
  title?: string;
  headingLevel?: "h1" | "h2";
}) {
  const message = errorCopy(error, {
    notFound: "This item is not available to this organization.",
    fallback: "Nothing was changed. Try again, or return to the previous page.",
  });
  return (
    <section role="alert" className="space-y-4 px-4 py-16 sm:px-6">
      {headingLevel === "h1" ? (
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
      ) : (
        <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
      )}
      <p className="max-w-prose text-sm leading-relaxed text-fg-muted">
        {message}
      </p>
      <Button variant="outline" onClick={retry}>
        <RefreshCw className="size-4" aria-hidden="true" />
        Try again
      </Button>
    </section>
  );
}

const lifecycleCopy: Record<CharacterLifecycle, string> = {
  draft: "Draft",
  building: "Building",
  candidate: "Candidate",
  accepted: "Accepted",
  archived: "Archived",
};

export function LifecycleBadge({ state }: { state: CharacterLifecycle }) {
  // `candidate` is the only lifecycle waiting on a person. `failed` and
  // `unknown` are VERSION states, not character states, and are rendered
  // where the version itself is shown.
  const warning = state === "candidate";
  const success = state === "accepted";
  return (
    <span
      className={cn(
        "rounded-pill border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide",
        warning && "border-warning/40 bg-warning/10 text-warning",
        success && "border-success/40 bg-success/10 text-success",
        !warning && !success && "border-border text-fg-muted",
      )}
    >
      {lifecycleCopy[state] ?? state}
    </span>
  );
}

export function LocalMedia({
  orgSlug,
  mediaID,
  alt,
  thumbnail = false,
  className,
}: {
  orgSlug: string;
  mediaID: string;
  alt: string;
  thumbnail?: boolean;
  className?: string;
}) {
  return (
    <img
      src={mediaUrl(orgSlug, mediaID, thumbnail ? "thumbnail" : "content")}
      alt={alt}
      className={cn("bg-bg-sunken object-cover", className)}
    />
  );
}

/**
 * One failed mutation, said once, in our words.
 *
 * The server's rejected field remains actionable even without custom copy.
 * `fieldCopy` can explain a field in the surface's own vocabulary, but omitting
 * that table must not turn a deterministic rejection into “try again”.
 */
export function MutationMessage({
  error,
  staleCopy,
  fallbackCopy = "That change did not go through. Nothing was changed; try again.",
  codeCopy,
  fieldCopy,
}: {
  error: unknown;
  staleCopy: string;
  fallbackCopy?: string;
  codeCopy?: Readonly<Record<string, string>>;
  fieldCopy?: Readonly<Record<string, string>>;
}) {
  if (!error) return null;
  const stale =
    isApiError(error) && (error.status === 412 || error.code === "stale_etag");
  const field = validationField(error);
  const override = isApiError(error) ? codeCopy?.[error.code] : undefined;
  const memberReason = memberRefusalMessage(error);
  return (
    <p
      role="alert"
      className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {stale
        ? staleCopy
        : memberReason !== undefined
          ? memberReason
          : field !== undefined
            ? (fieldCopy?.[field] ??
              `The server refused the value of “${field}”. Correct that field and try again.`)
            : (override ??
              errorCopy(error, {
                notFound: "This item is not available to this organization.",
                fallback: fallbackCopy,
              }))}
    </p>
  );
}
